import InfoIcon from '@mui/icons-material/Info'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverList from 'components/KNDetailsPopover/KNDetailsPopoverList'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { KNDangerousGoodsIcon, KNSharedViaEmailIcon, KNSharedViaSMSIcon, KNSharedViaWhatsAppIcon } from 'components/KNIcon/KNMaterialIcon'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { localDate } from 'global/helpers/dates'
import { hasHighlights, replaceHighlights } from 'global/helpers/highlights'
import React, { type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import AutomaticStatusChip from 'screens/TripDetails/AutomaticStatusChip'
import DecaQrCode from 'screens/TripDetails/DecaQrCode'
import Temperature from 'screens/TripDetails/Temperature'
import Tracking from 'screens/TripDetails/Tracking'
import { type LegData, PrincipalId } from 'screens/TripDetails/TripDetails.types'
import type { TripData } from './TripDashboard.types'
import TripStatusChip from './TripStatusChip'

export interface TripHeaderProps {
  trip: TripData
  legs?: LegData[]
  linked?: boolean
  onLinkClick?: (trip: TripData) => void
  stacked?: boolean
  withStatus?: boolean
  size?: 'large'
  weblinkToken?: string
  highlights?: string[]
}

export const getLastShareIcon = (channel: string): ReactElement => {
  switch (channel) {
    case 'SMS':
      return <KNSharedViaSMSIcon />
    case 'WHATSAPP':
      return <KNSharedViaWhatsAppIcon />
    case 'EMAIL':
    default:
      return <KNSharedViaEmailIcon />
  }
}

const hasAutomaticUpdates = (legs: LegData[]) => {
  return legs.some(leg => leg.wayPoints.some(wayPoint => wayPoint.statuses?.some(status => status.principalId === PrincipalId.SYSTEM)))
}
const TripHeader = ({
  trip,
  legs,
  linked = false,
  onLinkClick,
  stacked = false,
  withStatus = true,
  size,
  weblinkToken,
  highlights,
}: TripHeaderProps): ReactElement => {
  const { t } = useTranslation()
  const { processedReferences, processedCarrierInstructions, isHighlighted } = useMemo(() => ({
    processedReferences: trip.customerReferences.map(reference => replaceHighlights(reference, highlights)),
    processedCarrierInstructions: replaceHighlights(trip.carrierInstructions, highlights),
    isHighlighted: trip.customerReferences.some(reference => hasHighlights(reference, highlights))
      || hasHighlights(trip.carrierInstructions, highlights),
  }), [trip.customerReferences, trip.carrierInstructions, highlights])

  const {
    anchorEl: tripDetailsAnchorEl,
    open: tripDetailsOpen,
    handleClick: handleTripDetailsClick,
    handleClose: handleTripDetailsClose,
  } = usePopoverState()
  const {
    anchorEl: dangerousDetailsAnchorEl,
    open: dangerousDetailsOpen,
    handleClick: handleDangerousDetailsClick,
    handleClose: handleDangerousDetailsClose,
  } = usePopoverState()

  const handleLinkClick = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    onLinkClick?.(trip)
  }, [])

  const handleTripDetailsClickWrapper = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    handleTripDetailsClick(event)
    analyticsEvent('polestar_cs_trip_details_popover', [getRouteName(location.pathname)])
  }, [])

  const handleDangerousDetailsClickWrapper = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    handleDangerousDetailsClick(event)
    analyticsEvent('polestar_cs_trip_dangerous_goods_popover', [getRouteName(location.pathname)])
  }, [])

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Stack data-guide="trip-summary" direction="column" alignItems="start" spacing={0.5}>
        {stacked && <Stack direction="row" spacing={1} sx={{ width: '100%', maxWidth: '100%' }}>
          <TripStatusChip status={trip.status} />
          {legs && hasAutomaticUpdates(legs) && <AutomaticStatusChip />}
        </Stack>}
        <Stack spacing={1} direction="row" alignItems="center" sx={{ width: '100%', maxWidth: '100%' }}>
          {linked ? (
            <Link
              component={RouterLink}
              to={`/trips/${trip.entityId}`}
              color="primary.main"
              variant="h6"
              onClick={handleLinkClick}
              className="hover-link"
              noWrap
            >
              {replaceHighlights(trip.voyageNumber, highlights)}
            </Link>
          ) : (
            <Typography color="primary.main" variant={size === 'large' ? 'h6' : 'p2'} noWrap>
              {replaceHighlights(trip.voyageNumber, highlights)}
            </Typography>
          )}
          {!stacked && withStatus && <TripStatusChip status={trip.status} />}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: stacked ? 'auto' : '170px' }}>
            <IconButton
              onClick={handleTripDetailsClickWrapper}
              size="small"
              color={isHighlighted ? 'warning' : 'primary'}
              sx={{ padding: 0 }}
            >
              {isHighlighted ? <InfoIcon /> : <InfoOutlinedIcon />}
            </IconButton>
            {trip.dangerousGoodsDetails.length > 0 && (
              <IconButton
                onClick={handleDangerousDetailsClickWrapper}
                size="small"
                color="error"
                sx={{ padding: 0 }}
              >
                <KNDangerousGoodsIcon />
              </IconButton>
            )}
            <DecaQrCode trip={trip} />
            <Tracking trip={trip} weblinkToken={weblinkToken} />
            <Temperature trip={trip} weblinkToken={weblinkToken} withGraph />
            {(trip.lastShareChannel && trip.lastShareTimestamp) && (
              <Tooltip
                title={t(`screens.cs.trip_dashboard.share.last_shared_via.${trip.lastShareChannel}`, {
                  timestamp: localDate(trip.lastShareTimestamp, 'full_no_year'),
                })}
                enterDelay={200}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ padding: 0 }}
                >
                  {getLastShareIcon(trip.lastShareChannel)}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
      <KNDetailsPopover
        open={tripDetailsOpen}
        onClose={handleTripDetailsClose}
        anchorEl={tripDetailsAnchorEl}
        rightAligned
      >
        <KNDetailsPopoverList
          items={[
            {
              label: t('screens.cs.trip_dashboard.card.created_on'),
              value: localDate(trip.creationDate) ?? undefined,
            },
            ...(trip.carrierInstructions
              ? [
                {
                  label: t('screens.cs.trip_dashboard.trip_details.instructions'),
                  value: processedCarrierInstructions,
                  stacked: true,
                },
              ]
              : []),
            ...(processedReferences.length > 0
              ? [
                {
                  label: t('screens.cs.trip_dashboard.trip_details.references'),
                  value: processedReferences,
                  stacked: true,
                },
              ]
              : []),
            {
              label: t('screens.cs.trip_dashboard.trip_details.kn_branch'),
              value: trip.mappedSender,
            },
          ]}
        />
      </KNDetailsPopover>
      {trip.dangerousGoodsDetails.length > 0 && (
        <KNDetailsPopover
          open={dangerousDetailsOpen}
          onClose={handleDangerousDetailsClose}
          anchorEl={dangerousDetailsAnchorEl}
          rightAligned
        >
          {trip.dangerousGoodsDetails.map((tripDetails) => (
            <KNDetailsPopoverList
              key={tripDetails.description}
              description={tripDetails.description}
              items={[
                {
                  label: t('screens.cs.trip_details.dangerous_goods.packages'),
                  value: tripDetails.packagesType
                    ? `${tripDetails.numberOfPackages} × ${tripDetails.packagesType}`
                    : tripDetails.numberOfPackages,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.un_number'),
                  value: tripDetails.unNumber,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.class'),
                  value: tripDetails.classType,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.packing_group'),
                  value: tripDetails.packingGroup,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.quantity'),
                  value: tripDetails.quantityType ? `${tripDetails.quantity} × ${tripDetails.quantityType}` : tripDetails.quantity,
                },
              ]}
            />
          ))}
        </KNDetailsPopover>
      )}
    </Box>
  )
}

export default TripHeader
